import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import AutomationLogsPage, { type AutomationLogsSearchState } from "./-automationLogsPage/automationLogsPage";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { queryClient, trpc } from "~/utils/trpc";
import { hasPermission } from "~/store/usePermissionStore";
import { z } from "zod";

export const Route = createFileRoute("/_dashboard/$datasetSlug/automations/$automationId/logs")({
  beforeLoad: async ({ params }) => {
    const session = await queryClient.fetchQuery(trpc.auth.getSession.queryOptions()).catch(() => null);

    if (!hasPermission({ role: session?.user?.role, permissions: session?.user?.permissions }, "automations", "view")) {
      throw redirect({ to: "/$datasetSlug/dashboard", params: { datasetSlug: params.datasetSlug } });
    }
  },
  validateSearch: z.object({
    action: z.string().optional(),
    recordType: z.string().optional(),
    query: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { automationId, datasetSlug } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  useBreadCrumbs([
    { title: "Automations", link: `/${datasetSlug}/automations` },
    { title: "Details", link: `/${datasetSlug}/automations/${automationId}` },
    { title: "Logs", link: `/${datasetSlug}/automations/${automationId}/logs` },
  ]);

  const handleSearchChange = useCallback(
    (patch: Partial<AutomationLogsSearchState>) => {
      navigate({
        search: (previous) => {
          const next = { ...previous, ...patch };
          return Object.fromEntries(
            Object.entries(next).filter(([, value]) => value !== undefined && value !== ""),
          ) as AutomationLogsSearchState;
        },
        replace: true,
      });
    },
    [navigate],
  );

  return (
    <AutomationLogsPage
      automationId={automationId}
      datasetSlug={datasetSlug}
      search={search}
      onSearchChange={handleSearchChange}
    />
  );
}
